import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, Button, Badge, KV, Icon, Center, Spinner, Field, Chip, SuccessCard } from "@/components/ui";
import { DatePicker, TimePicker } from "@/components/DatePicker";
import { Topbar, PortalActions, SiteFooter } from "@/components/Layout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PAY_SOURCE_LABELS = { out_of_pocket: "Out of pocket", private_insurance: "Private insurance", ltc_insurance: "Long-term care (LTC) insurance", medicaid: "Medicaid", va_benefits: "VA benefits", not_sure: "Not sure" };
const CONTRACT_PATHS = [{ v: "sign_online", t: "Sign online" }, { v: "talk_specialist", t: "Talk to a specialist first" }, { v: "in_person_meeting", t: "Request an in-person meeting" }];

export default function ClientPortal() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [done, setDone] = useState(null);
  const [bookDate, setBookDate] = useState("");
  const [bookTime, setBookTime] = useState("09:00");
  const [reschedule, setReschedule] = useState(false);
  const [dType, setDType] = useState(null);
  const [dText, setDText] = useState("");
  const [reply, setReply] = useState({});
  const [paying, setPaying] = useState(false);
  const [setupSrc, setSetupSrc] = useState("");
  const [setupContract, setSetupContract] = useState("");
  const [aMode, setAMode] = useState("");
  const [aSlot, setASlot] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api.get("/client/portal")); } catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const sid = params.get("session_id");
    if (params.get("deposit") !== "success" || !sid) return;
    let tries = 0;
    const poll = async () => {
      try {
        const s = await api.get(`/client/deposit/status?session_id=${sid}`);
        if (s.payment_status === "paid") {
          setDone({
            title: "Deposit received — your assessment is confirmed",
            message: "Your $200 refundable deposit is in. Your care coordinator has everything needed to finalize your plan.",
            steps: ["A coordinator reviews and finalizes your care plan.", "Care begins 24 hours after the plan is finalized — day one is a free trial.", "You'll get a coordinator check-in call on your start day."],
            actions: [{ testID: "done-view-plan", label: "View my care plan", icon: "file-text", onClick: () => { setDone(null); nav("/careplan"); } }, { testID: "done-dashboard", label: "Back to dashboard", onClick: () => setDone(null) }],
          });
          window.scrollTo({ top: 0, behavior: "smooth" });
          load(); setParams({}); return;
        }
        if (s.payment_status === "expired") { flash("Payment session expired. Please try again."); setParams({}); return; }
      } catch {}
      if (++tries < 6) setTimeout(poll, 2000);
    };
    poll();
  }, [params]); // eslint-disable-line

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(null), 3500); };
  const act = async (fn, message) => { try { await fn(); flash(message); load(); } catch (e) { flash(e.message || "Failed"); } };
  const actDone = async (fn, doneObj) => { try { await fn(); setDone(doneObj); window.scrollTo({ top: 0, behavior: "smooth" }); load(); } catch (e) { flash(e.message || "Failed"); } };

  const payDeposit = async () => {
    setPaying(true);
    try {
      const r = await api.post("/client/deposit/checkout", { origin_url: window.location.origin, native: false });
      window.location.href = r.url;
    } catch (e) { flash(e.message || "Could not start payment"); setPaying(false); }
  };

  const shareReferral = () => {
    if (!data.referral_code) { flash("Your referral code isn't ready yet — check back shortly."); return; }
    const text = `Join HomecareMatches and get 8 hours of care free after your first 5 days of service! Use my referral code ${data.referral_code} when you sign up.`;
    if (navigator.share) navigator.share({ text }).catch(() => {});
    else { navigator.clipboard?.writeText(text); flash("Referral message copied to clipboard."); }
  };

  if (loading || !data) return <div className="screen"><Topbar title="Your care" /><Center><Spinner /></Center></div>;

  const c = data.client, plan = data.care_plan, contract = data.contract, m = data.matching;
  const dep = data.deposit || { status: "unpaid" };
  const amount = `$${((data.deposit_amount_cents || 0) / 100).toFixed(0)}`;
  const bk = data.booking;
  const disputes = data.disputes || [];

  const submitBooking = async () => {
    if (!bookDate) { flash("Tap a date on the calendar."); return; }
    const start_at = `${bookDate}T${bookTime || "09:00"}:00`;
    if (reschedule && bk?.status === "booked") await act(() => api.post("/client/booking/cancel", { reason: "reschedule", reschedule_to: start_at }), "Start date rescheduled.");
    else await act(() => api.post("/client/booking", { start_at }), "Start date booked.");
    setReschedule(false); setBookDate("");
  };
  const openDispute = async () => {
    if (!dType) { flash("Choose what this is about."); return; }
    if (!dText.trim()) { flash("Tell us a little about the concern."); return; }
    await act(() => api.post("/client/disputes", { type: dType, description: dText.trim() }), "Concern logged — 5 business days to resolve or we reassign you.");
    setDType(null); setDText("");
  };
  const contactSupport = () => act(() => api.post("/tickets", { subject: "Question about my care request", body: "I'd like an update on my care request." }), "Message sent — a HomecareMatches specialist will follow up.");

  const MatchingCard = () => m ? (
    <Card testID="matching-tracker">
      <div className="tag">YOUR CARE REQUEST</div>
      <div style={{ fontSize: 20, fontWeight: 800 }}>{m.current_title}</div>
      <p className="summary">{m.current_message}</p>
      {data.submitted_at ? <KV k="submitted" v={new Date(data.submitted_at).toLocaleString()} mono /> : null}
      {m.next_step_title ? <KV k="next step" v={m.next_step_title} /> : null}
      <div style={{ marginTop: 12 }}>
        {(m.steps || []).map((s) => (
          <div key={s.index} className="track-row" data-testid={`track-step-${s.index}`}>
            <Icon name={s.state === "done" ? "check-circle" : s.state === "active" ? "loader" : "circle"} size={20} color={s.state === "done" ? "var(--success)" : s.state === "active" ? "var(--brand)" : "var(--border-strong)"} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: s.state === "active" ? 800 : 700, color: s.state === "upcoming" ? "var(--muted)" : s.state === "active" ? "var(--brand)" : "var(--on-surface)" }}>{s.title}</div>
              {s.state === "active" ? <div className="note" style={{ margin: 0 }}>{s.message}</div> : null}
            </div>
          </div>
        ))}
      </div>
      <div className="reassure"><Icon name="shield" size={16} />{m.reassurance}</div>
      <Button testID="matching-support" variant="secondary" icon="message-circle" label="Contact HomecareMatches support" onClick={contactSupport} />
    </Card>
  ) : null;

  const saveSetup = async () => {
    const src = setupSrc || c.payment_source;
    const cpath = setupContract || c.contract_path;
    if (!src || !cpath) { flash("Choose how care will be paid for and a contract path."); return; }
    await actDone(() => api.post("/client/intake-details", { payment_source: src, contract_path: cpath }), {
      title: "Payment source & contract saved",
      message: "Step 1 is complete. You're ready to book your assessment.",
      steps: ["Next: schedule your video or in-person assessment below.", "Then pay the $200 refundable deposit to lock it in."],
      actions: [{ testID: "done-goto-assessment", label: "Schedule assessment", icon: "calendar", onClick: () => setDone(null) }],
    });
  };
  const scheduleAssessment = async () => {
    const mode = aMode || data.assessment?.mode;
    if (!mode) { flash("Choose a video or in-person assessment."); return; }
    await actDone(() => api.post("/client/assessment/schedule", { mode, slot: aSlot || data.assessment?.scheduled_at || null }), {
      title: "Assessment scheduled",
      message: "Your assessment is booked. One more step to confirm it.",
      steps: ["Pay the $200 refundable deposit to confirm your assessment slot.", "The deposit is fully refundable and applied to your first invoice."],
      actions: [{ testID: "done-pay-deposit", label: "Pay $200 deposit", icon: "credit-card", onClick: () => { setDone(null); payDeposit(); } }, { testID: "done-later", label: "I'll do it later", onClick: () => setDone(null) }],
    });
    setAMode(""); setASlot("");
  };

  const detailsDone = !!(c.payment_source && c.contract_path);
  const SetupCard = () => (
    <Card testID="setup-card">
      <div className="tag">STEP 1 · HOW CARE WILL BE PAID</div>
      {detailsDone ? <>
        <Badge testID="setup-status" label="payment & contract saved" tone="success" />
        <KV k="payment source" v={PAY_SOURCE_LABELS[c.payment_source] || c.payment_source} />
        <KV k="contract path" v={(CONTRACT_PATHS.find((x) => x.v === c.contract_path) || {}).t || c.contract_path} />
      </> : <>
        <p className="summary">Tell us how care will be paid for and how you'd like to handle the agreement.</p>
        <div className="tag">PAYMENT SOURCE</div>
        <div className="chip-wrap">{Object.entries(PAY_SOURCE_LABELS).map(([k, v]) => <Chip key={k} testID={`setup-src-${k}`} label={v} selected={(setupSrc || c.payment_source) === k} onClick={() => setSetupSrc(k)} />)}</div>
        <div className="tag">CONTRACT PATH</div>
        <div className="chip-wrap">{CONTRACT_PATHS.map((x) => <Chip key={x.v} testID={`setup-contract-${x.v}`} label={x.t} selected={(setupContract || c.contract_path) === x.v} onClick={() => setSetupContract(x.v)} />)}</div>
        <Button testID="save-setup" icon="check" label="Save" onClick={saveSetup} />
      </>}
    </Card>
  );

  const assessmentScheduled = !!(data.assessment && data.assessment.scheduled_at);
  const ScheduleCard = () => (
    <Card testID="schedule-card">
      <div className="tag">STEP 2 · BOOK YOUR ASSESSMENT</div>
      {assessmentScheduled ? (
        <p className="summary">Your {data.assessment.mode === "video" ? "video" : "in-person"} assessment is scheduled{data.assessment.scheduled_at ? ` for ${new Date(data.assessment.scheduled_at).toLocaleString()}` : ""}. {data.deposit?.status === "paid" ? "It's confirmed." : "Pay the $200 deposit below to confirm it."}</p>
      ) : <>
        <p className="summary">Choose your assessment. Your appointment is confirmed once the $200 deposit is paid.</p>
        <div className="chip-wrap">
          <Chip testID="sched-mode-in_person" label="In-person" selected={aMode === "in_person"} onClick={() => setAMode("in_person")} />
          <Chip testID="sched-mode-video" label="Video" selected={aMode === "video"} onClick={() => setAMode("video")} />
        </div>
        {aMode ? <>
          <div className="tag">PICK A TIME</div>
          {(data.assessment_slots || []).length === 0 ? <p className="note">No open slots right now — a coordinator will call to schedule.</p> :
            <div className="chip-wrap">{(data.assessment_slots || []).slice(0, 12).map((s) => <Chip key={s} testID={`sched-slot-${s}`} label={new Date(s).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric" })} selected={aSlot === s} onClick={() => setASlot(s)} />)}</div>}
          <Button testID="schedule-assessment" icon="calendar" label="Schedule assessment" onClick={scheduleAssessment} disabled={!aMode} />
        </> : null}
      </>}
    </Card>
  );

  const priorRefund = data.prior_deposit_refund;
  const DepositCard = () => (
    <Card testID="deposit-card">
      <div className="tag">ASSESSMENT DEPOSIT</div>
      {priorRefund ? <div className="banner" data-testid="prior-refund-note" style={{ marginBottom: 10 }}>
        You requested a new agency. Your previous agency (<b>{priorRefund.agency_name}</b>) {priorRefund.status === "refunded" ? "has refunded" : "will refund"} your original {amount} deposit. A new {amount} deposit is required for your new agency below.
      </div> : null}
      <Badge testID="deposit-status" label={`deposit: ${dep.status}`} tone={dep.status === "paid" ? "success" : dep.status === "forfeited" ? "error" : "warning"} />
      <p className="summary">A <b className="bold-brand">{amount}</b> deposit confirms your assessment — credited to your matched agency and refundable if you cancel 4+ hours before. You also get <b className="bold-brand">1 free day (up to 8 hours)</b>, earned after your first 5 days of service. {data.cancellation_policy}</p>
      {data.deposit?.credited_to_agency ? <KV k="credited to" v={data.deposit.credited_to_agency} mono /> : null}
      {data.hours?.forfeited ? <Badge label="deposit forfeited (late cancellation)" tone="error" /> : null}
      {dep.status !== "paid" && dep.status !== "refunded" ? <Button testID="pay-deposit" icon="credit-card" label={`Pay ${amount} deposit to confirm assessment`} onClick={payDeposit} loading={paying} /> : null}
    </Card>
  );

  const BookingCard = () => (
    <Card testID="booking-card">
      <div className="tag">YOUR START DATE</div>
      {bk?.status === "booked" ? <>
        <Badge testID="booking-status" label={`booked · ${new Date(bk.start_at).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}`} tone="success" />
        <p className="note">{data.cancellation_policy}</p>
        <div className="btn-row" style={{ marginTop: 8 }}>
          <Button small testID="reschedule-booking" variant="secondary" icon="calendar" label={reschedule ? "Close" : "Reschedule"} onClick={() => setReschedule(!reschedule)} />
          <Button small testID="cancel-booking" variant="ghost" icon="x" label="Cancel start" onClick={() => act(() => api.post("/client/booking/cancel", { reason: "client cancelled" }), "Booking cancelled.")} />
        </div>
      </> : <p className="summary">{bk?.status === "cancelled_by_platform" ? "HomecareMatches reassigned you to a new agency — nothing was forfeited. Book your new start date once matched." : dep.status === "paid" ? "Your deposit is in and assessment confirmed — pick the day your care begins (at least 24 hours after your assessment, and 48+ hours out)." : "Pay the deposit above to confirm your assessment and unlock booking."}</p>}
      {(bk?.status !== "booked" || reschedule) && dep.status === "paid" ? <>
        <div className="tag" style={{ marginTop: 12 }}>PICK A START DATE</div>
        <DatePicker testID="book-date" value={bookDate} onChange={setBookDate} minDate={new Date(Date.now() + 48 * 3600 * 1000)} />
        <div className="tag">START TIME</div>
        <TimePicker testID="book-time" value={bookTime} onChange={setBookTime} />
        <Button testID="book-submit" icon="check" label={reschedule ? "Confirm new start date" : "Book start date"} onClick={submitBooking} disabled={!bookDate} />
      </> : null}
    </Card>
  );

  const cp = data.care_preferences;
  const PrefsCard = () => cp ? (
    <Card testID="prefs-card">
      <div className="tag">YOUR CARE PREFERENCES RECORD (v{cp.version})</div>
      <p className="note">Built by your HomecareMatches nurse. Non-clinical only — it travels with you to any agency you're matched or reassigned to.</p>
      <KV k="support categories" v={(cp.care_category_labels || []).join(", ") || "—"} />
      <KV k="schedule" v={`${(cp.days || []).join(", ") || "—"} · ${cp.start_time || "?"}–${cp.end_time || "?"} · ${cp.hours_per_week || "?"} hrs/wk`} />
      <KV k="language" v={cp.language || "—"} />
      <KV k="cultural fit" v={(cp.cultural_fit || []).join(", ") || "—"} />
      <KV k="personality fit" v={cp.personality_fit || "—"} />
    </Card>
  ) : null;

  const ReferralCard = () => (
    <Card testID="referral-card">
      <div className="tag">REFER A FRIEND</div>
      <p className="summary">Refer another family — they get <b className="bold-brand">8 free hours</b> after their first 5 days of service, and you earn <b className="bold-brand">4 free hours</b> added to your care.</p>
      <KV k="your code" v={data.referral_code || "—"} mono />
      <KV k="free hours earned" v={`${data.referral_hours_earned || 0} hrs`} mono />
      <Button testID="share-referral" variant="secondary" icon="share-2" label="Share your referral code" onClick={shareReferral} />
    </Card>
  );

  const AssessmentCard = () => data.assessment ? (
    <Card testID="assessment-card">
      <div className="tag">{data.assessment.mode === "video" ? "HOMECAREMATCHES NURSE VIDEO CALL" : "ASSESSMENT"}</div>
      {data.assessment.mode === "video" ? <p className="note">A HomecareMatches registered nurse joins you for a general care-needs and safety conversation — not a clinical intake.</p> : null}
      <Badge label={data.assessment.completed_at ? "completed" : `${data.assessment.mode} • scheduled`} tone={data.assessment.completed_at ? "success" : "brand"} />
      {data.assessment.scheduled_at ? <KV k="when" v={new Date(data.assessment.scheduled_at).toLocaleString()} mono /> : null}
      {!data.assessment.completed_at && data.assessment.mode === "in_person" ? <>
        <div className="tag">RESCHEDULE — NO DOCUMENTS NEEDED</div>
        <div className="chip-wrap">{(data.assessment_slots || []).slice(0, 8).map((s) => <Chip key={s} testID={`client-slot-${s}`} label={new Date(s).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric" })} onClick={() => act(() => api.post("/client/assessment/reschedule", { slot: s }), "Assessment rescheduled.")} />)}</div>
      </> : null}
    </Card>
  ) : null;

  const DisputesCard = () => (
    <Card testID="disputes-card">
      <div className="tag">CONCERNS ABOUT YOUR AGENCY OR CAREGIVER</div>
      <p className="note">Contact HomecareMatches any time — not just on day one. Every concern has a 5-business-day resolution clock; if it isn't resolved, we automatically reassign you.</p>
      {disputes.map((d) => (
        <div key={d.id} className="dispute-row" data-testid={`dispute-${d.id}`}>
          <div className="between"><b>{d.type_label}</b><Badge label={d.status === "open" ? (d.sla?.sla_label || "open") : d.status} tone={d.status === "open" ? (d.sla?.overdue ? "error" : "warning") : d.status === "reassigned" ? "brand" : "success"} /></div>
          <p className="msg-line">{d.description}</p>
          {(d.messages || []).map((mm) => <p key={mm.id} className="msg-line"><b className="bold-brand">{mm.from_role === "client" ? "You" : mm.from_role === "agency" ? d.agency_name : "HomecareMatches"}:</b> {mm.body}</p>)}
          {d.resolution_note ? <p className="msg-line"><b className="bold-brand">Resolution:</b> {d.resolution_note}</p> : null}
          {d.status === "open" ? (
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginTop: 6 }}>
              <div style={{ flex: 1 }}><Field testID={`reply-${d.id}`} value={reply[d.id] || ""} onChange={(v) => setReply({ ...reply, [d.id]: v })} placeholder="Reply…" /></div>
              <Button small testID={`send-reply-${d.id}`} variant="secondary" label="Send" onClick={() => act(() => api.post(`/client/disputes/${d.id}/messages`, { body: reply[d.id] || "" }), "Reply sent.").then(() => setReply({ ...reply, [d.id]: "" }))} />
            </div>
          ) : null}
        </div>
      ))}
      {data.can_open_dispute ? <>
        <div className="tag" style={{ marginTop: 12 }}>NEW CONCERN OR REQUEST</div>
        <div className="chip-wrap">{Object.entries(data.dispute_types || {}).map(([k, v]) => <Chip key={k} testID={`dtype-${k}`} label={v} selected={dType === k} onClick={() => setDType(k)} />)}</div>
        <Field testID="dispute-text" label="What's going on? (no medical details needed)" value={dText} onChange={setDText} placeholder="e.g. Caregiver arrived late twice this week" multiline />
        <Button testID="open-dispute" icon="alert-circle" variant="secondary" label="Send to HomecareMatches" onClick={openDispute} />
      </> : <p className="note">Once an agency is assigned you can raise concerns or request a new caregiver / agency here.</p>}
    </Card>
  );

  return (
    <div className="screen" data-testid="client-portal">
      <Topbar title="Your care" subtitle={user?.full_name} right={<PortalActions />} />
      <div className="container-narrow">
        {msg ? <div className="toast" data-testid="client-toast">{msg}</div> : null}
        {done ? <SuccessCard testID="client-success" title={done.title} message={done.message} steps={done.steps} actions={done.actions} onClose={() => setDone(null)} /> : null}
        <MatchingCard />
        {data.portal_locked ? <>
          <Card testID="portal-locked">
            <div className="tag">ACCOUNT STATUS</div>
            <Badge label={`verification: ${data.verification_status}`} tone={data.verification_status === "verification_pending" ? "brand" : "warning"} />
            <p className="summary">{data.message}</p>
            <p className="note">Care plan, records, pricing, and support actions unlock once a coordinator confirms your account.</p>
          </Card>
          <AssessmentCard /><SetupCard /><ScheduleCard /><DepositCard /><BookingCard /><PrefsCard /><ReferralCard />
        </> : <>
          <Card testID="careplan-card">
            <div className="tag">CARE PLAN</div>
            {plan?.status === "needs_review" ? <Badge label="drafted by system — needs review" tone="warning" /> : <Badge label={plan?.status || "—"} tone="success" />}
            <p className="summary">{plan?.summary}</p>
            <KV k="care type" v={c.care_type} mono />
            {data.budget_band ? <KV k="your budget" v={data.budget_band} mono /> : null}
            {plan?.services_start_at ? <KV k="service start" v={new Date(plan.services_start_at).toLocaleString()} mono /> : null}
            {data.pricing_note ? <p className="note" style={{ color: "var(--brand)", fontWeight: 600 }}>{data.pricing_note}</p> : null}
          </Card>
          <AssessmentCard />
          {data.day1 ? <Card testID="day1-card">
            <div className="tag">DAY 1 — WHO'S COMING</div>
            <div className="center-icon-row" style={{ marginTop: 6 }}><Icon name="users" size={16} color="var(--brand)" /><span>Caregiver{data.day1.caregiver_name ? `: ${data.day1.caregiver_name}` : ""}</span></div>
            <div className="center-icon-row" style={{ marginTop: 6 }}><Icon name="plus-circle" size={16} color="var(--brand)" /><span>{data.day1.agency_name}'s nurse — arriving together</span></div>
            {data.day1.start_at ? <KV k="when" v={new Date(data.day1.start_at).toLocaleString()} mono /> : null}
            <p className="note">{data.day1.message}</p>
          </Card> : null}
          <DepositCard /><BookingCard /><PrefsCard /><DisputesCard />
          <Card testID="authority-card">
            <div className="tag">YOUR ACCESS (based on relationship)</div>
            <KV k="relationship" v={data.authority?.basis || "—"} mono />
            <div className="center-icon-row" style={{ marginTop: 8 }}><Icon name={data.authority?.can_sign_agreement ? "check-circle" : "x-circle"} size={16} color={data.authority?.can_sign_agreement ? "var(--success)" : "var(--muted)"} /><span>Sign agreements</span></div>
            <div className="center-icon-row" style={{ marginTop: 8 }}><Icon name={data.authority?.can_receive_health_updates ? "check-circle" : "x-circle"} size={16} color={data.authority?.can_receive_health_updates ? "var(--success)" : "var(--muted)"} /><span>Receive care updates</span></div>
          </Card>
          <div className="section-label">UPDATES</div>
          {(data.updates || []).length === 0 ? <p className="note">No updates yet.</p> : (data.updates || []).map((u) => (
            <div key={u.id} className="update-row"><span className="update-dot" /><div><div style={{ fontSize: 14 }}>{u.message}</div><div className="row-sub">{new Date(u.created_at).toLocaleString()}</div></div></div>
          ))}
          <div className="section-label">ACTIONS</div>
          <div className="stack">
            <Button testID="request-replacement" variant="secondary" icon="refresh-cw" label="Request a replacement caregiver" onClick={() => act(() => api.post("/client/replacement-request"), "Replacement requested — a coordinator will follow up.")} />
            <Button testID="leave-feedback" variant="secondary" icon="star" label="Leave feedback" onClick={() => act(() => api.post("/client/feedback", { rating: 5, comment: "Great care" }), "Thank you for your feedback.")} />
            <Button testID="message-support" variant="secondary" icon="message-circle" label="Message support" onClick={() => act(() => api.post("/tickets", { subject: "Question about my care", body: "I'd like to discuss my schedule." }), "Message sent — routed to support.")} />
            {data.authority?.can_sign_agreement ? (
              <Button testID="sign-agreement" variant={contract?.status === "signed" ? "ghost" : "primary"} icon="edit-3" label={contract?.status === "signed" ? "Agreement signed ✓" : "Sign service agreement"}
                onClick={() => contract?.status === "signed" ? flash("Your agreement is already signed.") : act(() => api.post("/client/sign-agreement"), "Agreement signed — thank you.")} />
            ) : <Button testID="view-agreement" variant="ghost" icon="file-text" label={contract ? `View agreement (${contract.status})` : "View agreement"} onClick={() => flash(contract ? `Agreement status: ${contract.status}.` : "No agreement yet")} />}
          </div>
          <ReferralCard />
        </>}
        <div className="footer-links" style={{ justifyContent: "center", marginTop: 24 }}>
          <a data-testid="client-terms" onClick={() => nav("/terms")}>Terms of Service</a>
          <a data-testid="client-privacy" onClick={() => nav("/privacy")}>Privacy Policy</a>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}
